import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';

import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../components/DataTable/BasicPagination';
import PageSizeSelector from '../../components/DataTable/PageSizeSelector';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';

import useBranchScope from '../../hooks/branches/useBranchScope';
import useRestaurantTables from '../../hooks/restaurant/useRestaurantTables';
import useRestaurantTableTheme from '../../hooks/restaurant/useRestaurantTableTheme';
import useDebounced from '../../hooks/useDebounced';

import {
  RestaurantTableFilters,
  RestaurantTableStatus,
} from '../../types/restaurant/RestaurantTableTypes';

import {
  TableHeader,
  TableBranchEmpty,
  TableFilters,
  TableSkeleton,
  TableEmpty,
  TableGrid,
  RestaurantTableList,
  TableFormModal,
} from '../../features/restaurant/tables';
import WorkspaceLayout from '../../layout/WorkspaceLayout';

const RestaurantTables = () => {
  const {
    branches,
    branchId,
    setBranchId,
    loadingBranches,
    branchError,
    canPickBranch,
  } = useBranchScope();

  const [search, setSearch] = useState('');
  const [roomFilter, setRoomFilter] = useState(0);

  const [statusFilter, setStatusFilter] = useState<
    'ALL' | RestaurantTableStatus
  >('ALL');

  const [activeFilter, setActiveFilter] = useState<
    'ALL' | 'ACTIVE' | 'INACTIVE'
  >('ALL');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounced(search, 350);
  const normalizedSearch = search === '' ? '' : debouncedSearch;

  const tables = useRestaurantTables(branchId);
  useRestaurantTableTheme(branchId);

  useEffect(() => {
    tables.resetResults();
    return tables.resetResults;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = useMemo<RestaurantTableFilters>(
    () => ({
      search: normalizedSearch.trim() || null,
      diningRoomId: roomFilter || null,
      status: statusFilter === 'ALL' ? null : statusFilter,
      active: activeFilter === 'ALL' ? null : activeFilter === 'ACTIVE',
      page,
      size: pageSize,
    }),
    [activeFilter, normalizedSearch, page, pageSize, roomFilter, statusFilter]
  );

  useEffect(() => {
    if (branchId) {
      tables.loadOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (branchId) tables.load(filters, tables.page !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, filters]);

  const resetPage = <T,>(setter: (value: T) => void, value: T) => {
    setter(value);
    setPage(0);
  };

  const changeBranch = (id: number) => {
    tables.resetResults();
    setSearch('');
    setRoomFilter(0);
    setStatusFilter('ALL');
    setActiveFilter('ALL');
    setPage(0);
    setBranchId(id);
  };

  const previousPage = () => setPage((current) => Math.max(0, current - 1));
  const retry = () => tables.load(filters, tables.page !== null);

  if (!loadingBranches && !branchError && !branchId) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />

        <SectionWrapper>
          <TableBranchEmpty />
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <TableHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          onBranchChange={changeBranch}
          onCreate={tables.openCreate}
        />

        {branchId && (
          <>
            <TableFilters
              search={search}
              roomFilter={roomFilter}
              statusFilter={statusFilter}
              activeFilter={activeFilter}
              diningRooms={tables.diningRooms}
              onSearchChange={(value) => resetPage(setSearch, value)}
              onRoomChange={(value) => resetPage(setRoomFilter, value)}
              onStatusChange={(value) => resetPage(setStatusFilter, value)}
              onActiveChange={(value) => resetPage(setActiveFilter, value)}
            />

            {tables.loading ? (
              <TableSkeleton />
            ) : tables.error && !tables.page ? (
              <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center">
                <span className="mb-4 rounded-full bg-danger/10 p-4 text-danger">
                  <AlertCircle className="h-8 w-8" />
                </span>
                <h3 className="text-lg font-semibold text-black dark:text-white">
                  No fue posible cargar las mesas
                </h3>
                <p className="mt-2 text-sm text-bodydark2">{tables.error}</p>
                <button
                  type="button"
                  onClick={retry}
                  className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-medium text-white"
                >
                  <RefreshCw className="h-4 w-4" /> Reintentar
                </button>
              </div>
            ) : tables.list.length === 0 ? (
              <TableEmpty />
            ) : (
              <div className="relative">
                {tables.error && (
                  <div className="m-5 flex items-center justify-between gap-3 rounded-lg border border-danger/20 bg-danger/5 px-4 py-3 text-sm text-danger">
                    <span>{tables.error}</span>
                    <button
                      type="button"
                      onClick={retry}
                      className="font-semibold underline"
                    >
                      Reintentar
                    </button>
                  </div>
                )}
                {tables.updating && (
                  <div className="absolute right-5 top-3 z-10 flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-primary shadow dark:bg-boxdark">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />{' '}
                    Actualizando
                  </div>
                )}
                <RestaurantTableList
                  tables={tables.list}
                  onEdit={tables.edit}
                  onStatusChange={(table, status) =>
                    tables.updateStatus(table, status, filters, previousPage)
                  }
                  onActiveChange={(table, active) =>
                    tables.updateActive(table, active, filters, previousPage)
                  }
                />
                <TableGrid
                  tables={tables.list}
                  onEdit={tables.edit}
                  onStatusChange={(table, status) =>
                    tables.updateStatus(table, status, filters, previousPage)
                  }
                  onActiveChange={(table, active) =>
                    tables.updateActive(table, active, filters, previousPage)
                  }
                />
              </div>
            )}

            {tables.page && tables.page.totalElements > 0 && (
              <div className="flex flex-col gap-4 border-t border-stroke px-5 pb-5 dark:border-strokedark sm:flex-row sm:items-end sm:justify-between">
                <p className="pb-2 text-sm font-medium text-bodydark2">
                  {tables.page.totalElements.toLocaleString('es-SV')} mesas
                  encontradas
                </p>
                <BasicPagination
                  currentPage={tables.page.number}
                  totalPages={tables.page.totalPages}
                  onPageChange={setPage}
                />
                <PageSizeSelector
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(0);
                  }}
                />
              </div>
            )}
          </>
        )}
      </SectionWrapper>

      <TableFormModal
        isOpen={tables.modalOpen}
        form={tables.form}
        diningRooms={tables.diningRooms}
        saving={tables.saving}
        onClose={tables.closeModal}
        onChange={tables.updateField}
        onSave={() => tables.save(filters, previousPage)}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantTables;
